import { createApp } from 'vue'

import 'normalize.css/normalize.css' // A modern alternative to CSS resets

import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'

// antdv4 的日期面板头部/星期/月份名由 dayjs 格式化（如 YYYY年MMM 的 MMM、dd），
// 只配 a-config-provider locale 不够，必须手动设置 dayjs locale，否则显示 "2026年Oct"、"Su Mo Tu"
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
dayjs.locale('zh-cn')

import '@/styles/index.scss' // global css

import App from './App'
import router from './router'
import store from './store'

import '@/permission' // permission control

import '@/assets/public/css/comIconfont/iconfont.css'
import '@/assets/public/css/comIconfont/iconfont.js'

//自定义的图标
import '@/assets/public/css/myIconFont/iconfont.css'
import '@/assets/public/css/myIconFont/iconfont.js'

import '../public/ueditor/ueditor.config.js'
import '../public/ueditor/ueditor.all.js'
import '../public/ueditor/lang/zh-cn/zh-cn.js'
import '../public/ueditor/formdesign/leipi.formdesign.v4.js'

//表单验证
import * as validater from '@/utils/validater.js'

import * as echarts from 'echarts'

//视频播放
import Video from 'video.js'
import 'video.js/dist/video-js.css'

import tools from '@/utils/tools'

import { checkPermission } from '@/utils/util.js'

// rc-form 兼容层（antd v1 的 this.$form.createForm 写法在 v4 已删除）
import { createForm } from '@/utils/formCompat'

// 引入组件库
import antd from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import { notification } from 'ant-design-vue'

import { ElMessage, ElMessageBox } from 'element-plus'

//树状列表（vue-table-with-tree-grid 的 Vue3 兼容包装，内部用 Element Plus 实现）
import TreeTable from '@/components/TreeTable/index.vue'

//打印（vue3-print-nb）
import print from 'vue3-print-nb'

const app = createApp(App)

// ── ResizeObserver loop 告警修复（两层防护）────────────────────────────
// 现象：控制台偶发 "ResizeObserver loop completed with undelivered notifications"。
// 根源：Element Plus 的 el-table 等组件内部用 ResizeObserver 监听尺寸并在回调里改布局，
// 同一帧内「观察 → 布局变化 → 再触发」会命中浏览器的 loop 检测而抛错（W3C 定义为
// 不可处理，布局结果仍正确，不影响功能，但会以 Uncaught Error 刷控制台）。
// 防护 1：把 ResizeObserver 回调延迟到下一帧执行，从源头避免同一帧内的循环；
// 防护 2：全局过滤这条特定消息兜底，其他错误照常抛出。
if ('ResizeObserver' in window) {
  const OriginalResizeObserver = window.ResizeObserver
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        requestAnimationFrame(() => callback(entries, observer))
      })
    }
  }
}
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    e.message === 'ResizeObserver loop limit exceeded') {
    e.stopImmediatePropagation()
    e.preventDefault()
  }
})

app.use(router)
app.use(store)
app.use(ElementPlus, { locale: zhCn })
app.use(antd)
app.use(print)

// 富文本编辑（vue-quill-editor 已由 @vueup/vue-quill 替代，在用到的地方局部引入）

//全局属性（Vue2 的 Vue.prototype → Vue3 的 globalProperties）
app.config.globalProperties.$validater = validater
app.config.globalProperties.$echarts = echarts
app.config.globalProperties.$video = Video
app.config.globalProperties.tools = tools
app.config.globalProperties.checkPermission = checkPermission
app.config.globalProperties.$notification = notification
app.config.globalProperties.$confirm = ElMessageBox.confirm
app.config.globalProperties.$message = ElMessage
app.config.globalProperties.$form = { createForm }

//全局注册组件
app.component('tree-table', TreeTable)

// 覆盖 Element Plus 的 el-switch：丢弃它在初始化（含 el-table 隐藏列预渲染空行）时伪造的 change 事件，
// 否则列表页一加载就会误调「修改状态」接口。
// 注意用 _context.components 直写覆盖：app.component() 同名再注册在 Vue 3.5 会报
// "Component ElSwitch has already been registered in target app" 警告，直写 context 行为相同且无警告
import ElSwitchCompat from '@/components/Compat/ElSwitch'
app._context.components.ElSwitch = ElSwitchCompat

// 对话框拖拽组件（替代 Vue2 的 v-el-drag-dialog 指令）：
// EP 的 ElDialog 根节点是 Teleport，Vue3 自定义指令挂在它上面钩子永不触发（还会触发
// "Runtime directive used on component with non-element root node" 警告），故改为组件封装。
// 模板里把 <el-dialog v-el-drag-dialog ...> 换成 <drag-dialog ...> 即可，用法一致。
import DragDialog from '@/components/Compat/DragDialog'
app.component('DragDialog', DragDialog)

// Element UI 2 的字体图标（el-icon-plus 等）在 Element Plus 已改为 SVG 组件，
// 这里把旧类名注册成同名组件，让 icon="el-icon-xxx" 的写法继续生效
import ElIconsCompat from '@/components/Compat/ElIcons'
app.use(ElIconsCompat)

// antd v4 删除了 <a-icon> 组件，图标改到 @ant-design/icons-vue；把旧写法映射成同名组件
import AIconsCompat from '@/components/Compat/AIcons'
app.use(AIconsCompat)

// cron 组件的 $t 翻译插件
import cronTranslate from '@/components/cron/mixins/plugins/translate'
app.use(cronTranslate)

app.mount('#app')
